import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, Check } from 'lucide-react';
import { cn } from '../../utils/cn';

const StatusFilterDropdown = ({
  value = null,
  options = [],
  placeholder = "Select Status",
  onChange,
  className,
  icon: Icon = Calendar,
  showIcon = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (optionValue) => {
    setSelectedValue(optionValue);
    onChange?.(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.value === selectedValue);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  return (
    <div ref={dropdownRef} className={cn("relative inline-block w-full", className)}>
      {/* Dropdown Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center justify-between h-10 px-3 rounded-md border border-gray-300",
          "bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          "transition-colors duration-200 w-full text-sm",
          selectedValue && "border-blue-500 bg-blue-50"
        )}
      >
        <div className="flex items-center gap-2 flex-1">
          {showIcon && Icon && (
            <Icon className="h-4 w-4 text-gray-500" />
          )}
          <span className={cn(
            "truncate",
            selectedValue ? "text-blue-700 font-medium" : "text-gray-700"
          )}>
            {displayText}
          </span>
        </div>
        <ChevronDown 
          className={cn(
            "h-4 w-4 ml-2 transition-transform duration-200",
            isOpen && "transform rotate-180",
            selectedValue ? "text-blue-600" : "text-gray-400"
          )} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="py-1 max-h-60 overflow-auto">
            {options.map((option) => {
              const isSelected = selectedValue === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm transition-colors duration-150",
                    "flex items-center justify-between",
                    isSelected 
                      ? "bg-blue-600 text-white font-medium" 
                      : "text-gray-700 hover:bg-gray-100",
                    option.disabled && "opacity-50 cursor-not-allowed"
                  )}
                  disabled={option.disabled}
                >
                  <span>{option.label}</span>
                  {isSelected && (
                    <Check className="h-4 w-4 ml-2" />
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Clear selection option */}
          {selectedValue && (
            <div className="border-t border-gray-200 pt-1">
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Clear Selection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StatusFilterDropdown;

